import { toast } from "react-toastify";
import { updateStateData } from "../Store/actions";
import store from "../Store/store";
import { sendApiRequest, sendApiRequestWithFile } from "./requestConfig";
import { allowedNavOptions, checkUserDetailsFromCookie } from "./userManagement";

export const fetchCompanies = async (requestedfor) => {
  const checkUserDetails = checkUserDetailsFromCookie();
  // Owner/company dropdown. Keep the caller's query and do not add the retailer view id.
  const checkCompanies = await sendApiRequest("companies/share-list?type=" + requestedfor, {}, "GET", { skipRetailerViewFilter: true });

  let response = {};

  if (checkCompanies.success === true) {
    let companyList = [];
    let companyIdOptionObj = {};

    let companyNameList = [];
    if (checkCompanies.response.length !== 0) {
      checkCompanies.response.forEach((company) => {
        companyNameList.push({
          id: company.id,
          label: company.name,
        });
        companyList.push({
          id: company.id,
          name: company.name,
          level: company.level,
          tree_path: company.tree_path,
        });
        // to fetch company details based on company idF
        companyIdOptionObj = {
          ...companyIdOptionObj,
          [company.id]: {
            id: company.id,
            name: company.name,
            level: company.level,
            tree_path: company.tree_path,
          },
        };
      });

      store.dispatch(
        updateStateData({
          company_name_list: companyNameList,
          company_list: companyList,
          company_id_option_obj: companyIdOptionObj,
        })
      );
    }
    response = {
      success: true,
    };
  } else {
    response = {
      success: false,
    };
  }
  return response;
};

// Normalize hierarchy API payload into selectable tree + id map.
// Root is the top node of the tree and is selectable like any other company.
const normalizeCompanyHierarchyPayload = (payload) => {
  const retailersRaw = Array.isArray(payload.retailers) ? payload.retailers : [];
  const companyById = {};

  const registerNode = (node, type) => {
    if (typeof node === "undefined" || node === null || typeof node.id === "undefined") {
      return null;
    }

    const normalized = {
      id: node.id,
      label: typeof node.name !== "undefined" ? node.name : String(node.id),
      name: typeof node.name !== "undefined" ? node.name : String(node.id),
      // Root is not sent with a level, but the selects expect one — treat it as level 0.
      level: typeof node.level !== "undefined" ? node.level : type === "root" ? 0 : undefined,
      tree_path: node.tree_path,
      type: type,
    };

    // Brand add/edit uses this to prune unshareable companies and grey ancestor headers.
    // Only copy a real boolean — missing/undefined keeps the row enabled.
    if (typeof node.has_shareable_campaign === "boolean") {
      normalized.has_shareable_campaign = node.has_shareable_campaign;
    }
    // Clip owner picker (view_campaign_active=true) uses this like brand shareable campaigns.
    if (typeof node.has_active_campaign === "boolean") {
      normalized.has_active_campaign = node.has_active_campaign;
    }

    companyById[node.id] = normalized;
    return normalized;
  };

  const retailers = retailersRaw
    .map((retailerGroup) => {
      let nationalSource = retailerGroup.national;
      // Flags may live on the { national, branches } wrapper; copy onto national for the dropdown.
      if (nationalSource != null) {
        const groupFlags = {};
        if (
          typeof nationalSource.has_shareable_campaign !== "boolean" &&
          typeof retailerGroup.has_shareable_campaign === "boolean"
        ) {
          groupFlags.has_shareable_campaign = retailerGroup.has_shareable_campaign;
        }
        if (
          typeof nationalSource.has_active_campaign !== "boolean" &&
          typeof retailerGroup.has_active_campaign === "boolean"
        ) {
          groupFlags.has_active_campaign = retailerGroup.has_active_campaign;
        }
        if (Object.keys(groupFlags).length > 0) {
          nationalSource = { ...nationalSource, ...groupFlags };
        }
      }

      const national = registerNode(nationalSource, "national");
      if (national === null) {
        return null;
      }

      const branches = (Array.isArray(retailerGroup.branches) ? retailerGroup.branches : [])
        .map((branch) => {
          const branchNode = registerNode(branch, "branch");
          if (branchNode === null) {
            return null;
          }

          const stores = (Array.isArray(branch.stores) ? branch.stores : [])
            .map((store) => registerNode(store, "store"))
            .filter((storeNode) => storeNode !== null);

          return {
            ...branchNode,
            children: stores,
          };
        })
        .filter((branchNode) => branchNode !== null);

      return {
        ...national,
        children: branches,
      };
    })
    .filter((retailerNode) => retailerNode !== null);

  // Root is registered too so it can be selected and resolved as a default owner.
  const rootNode = registerNode(payload.root, "root");

  // Selects render this as the whole tree: root first, retailers nested under it.
  // Without a root the tree stays retailer-first, exactly as before.
  const selectableTree =
    rootNode !== null ? [{ ...rootNode, children: retailers }] : retailers;

  return {
    root: rootNode,
    retailers: selectableTree,
    company_by_id: companyById,
  };
};

/**
 * GET companies/hierarchy-list — root → national → branch → store tree.
 * Root is the first option in the tree and is selectable like any other company.
 * @param {{ viewCampaignShare?: boolean, viewCampaignActive?: boolean }} [options]
 *   viewCampaignShare — view_campaign_share=true (brand user share picker).
 *   viewCampaignActive — view_campaign_active=true (clip owner picker).
 */
export const fetchCompanyHierarchy = async (options = {}) => {
  // The endpoint scopes the tree to the logged-in user on the backend, so no owner_id is sent.
  const params = new URLSearchParams();
  if (options.viewCampaignShare === true) {
    params.set("view_campaign_share", "true");
  }
  if (options.viewCampaignActive === true) {
    params.set("view_campaign_active", "true");
  }
  const queryString = params.toString();
  const hierarchyEndpoint = queryString
    ? `companies/hierarchy-list?${queryString}`
    : "companies/hierarchy-list";
  const hierarchyResponse = await sendApiRequest(hierarchyEndpoint, {}, "GET", { skipRetailerViewFilter: true });
  const apiSucceeded =
    typeof hierarchyResponse.success !== "undefined" && hierarchyResponse.success === true;

  // Live payload only — no dummy tree when the endpoint fails.
  if (apiSucceeded !== true) {
    return {
      success: false,
      retailers: [],
      company_by_id: {},
    };
  }

  const payload =
    typeof hierarchyResponse.response !== "undefined" ? hierarchyResponse.response : {};
  const normalized = normalizeCompanyHierarchyPayload(payload);

  store.dispatch(
    updateStateData({
      company_hierarchy: {
        root: normalized.root,
        retailers: normalized.retailers,
      },
      company_hierarchy_by_id: normalized.company_by_id,
    })
  );

  return {
    success: true,
    retailers: normalized.retailers,
    company_by_id: normalized.company_by_id,
  };
};

//to match campaign list records with the filters applied.
const matchClipRecords = (item) => {
  const currentStateData = store.getState().userSelections;
  const appliedFilters = currentStateData.company_list_filters;

  let itemMatch = false;

  // company name filter
  if (appliedFilters.company_name.length !== 0) {
    appliedFilters.company_name.forEach((filterWord) => {
      if (item.label === filterWord.label) {
        itemMatch = true;
      }
    });
  }

  return itemMatch;
};

export const filterCompanies = () => {
  const currentStateData = store.getState().userSelections;
  let filteredData = [];

  if (typeof currentStateData.company_name_list !== "undefined" && currentStateData.company_name_list.length !== 0) {
    currentStateData.company_name_list.forEach((item) => {
      const checkMatch = matchClipRecords(item);
      if (checkMatch === true) {
        filteredData.push(item);
      }
    });
  }

  store.dispatch(
    updateStateData({
      filtered_company_list: {
        company_list: filteredData,
      },
      company_list_filter_applied: true,
    })
  );

  return true;
};
